import {
  DbOrderDirection,
  InsureIQCoverageStatus,
  InsureIQPolicyTypes,
  InsureIQImportSource,
  RmaLgmTypeCodes,
  RmaLrpTypeCodes,
} from '@harvestiq/constants';
import { z, zCalendarDate } from '@harvestiq/zod';
import { dbLrpEndorsementSchema } from './db-lrp-endorsement';
import { dbLgmEndorsementSchema } from './db-lgm-endorsement';
import { zCoercedBoolean } from '@harvestiq/zod';
import { coverageSchema } from './coverage';
import { lrpCommodityCodeTypeCodePairSchema } from './lrp-commodity-code-type-code-pair';
import { lgmCommodityCodeTypeCodePairSchema } from './lgm-commodity-code-type-code-pair';

export const getCoveragesRequestSchema = z.object({
  type: z.nativeEnum(InsureIQPolicyTypes).optional(),
  typeCodes: z
    .array(z.nativeEnum(RmaLrpTypeCodes).or(z.nativeEnum(RmaLgmTypeCodes)))
    .optional(),
  commodityCodeTypeCodePairs: z
    .array(
      lrpCommodityCodeTypeCodePairSchema.or(lgmCommodityCodeTypeCodePairSchema)
    )
    .optional(),
  statuses: z.array(z.nativeEnum(InsureIQCoverageStatus)).optional(),
  policyIds: z.array(z.string().uuid()).optional(),
  userOwnerIds: z.array(z.string().uuid()).optional(),
  entityIds: z.array(z.string().uuid()).optional(),
  salesEffectiveDates: z.array(z.string()).optional(), // a list of specific sales effective dates
  salesEffectiveDateStart: zCalendarDate().optional(), // a range of sales effective dates - start date
  salesEffectiveDateEnd: zCalendarDate().optional(), // a range of sales effective dates - end date
  endDateStart: zCalendarDate().optional(), // a range of end dates - start date
  endDateEnd: zCalendarDate().optional(), // a range of end dates - end date
  reinsuranceYears: z.array(z.coerce.number()).optional(),
  importSource: z.nativeEnum(InsureIQImportSource).optional(),
  skip: z.coerce.number().int().nonnegative().optional(),
  limit: z.coerce.number().int().positive().optional(),
  orderBy: z
    .string()
    // Only allow known coverage fields
    .refine(
      (value: string) =>
        Object.keys(dbLrpEndorsementSchema.shape).includes(value) ||
        Object.keys(dbLgmEndorsementSchema.shape).includes(value),
      {
        message: 'Invalid orderBy field',
      }
    )
    .default('createdAt'),
  order: z.nativeEnum(DbOrderDirection).default(DbOrderDirection.DESC),
  includeReport: zCoercedBoolean().optional(),
  getLatestMarketData: zCoercedBoolean().optional(),
});

export type GetCoveragesRequest = z.infer<typeof getCoveragesRequestSchema>;
export type GetCoveragesRequestInput = z.input<
  typeof getCoveragesRequestSchema
>;
export const getCoveragesResponseSchema = z.object({
  coverages: z.array(coverageSchema),
  totalCount: z.number(),
});

export type GetCoveragesResponse = z.infer<typeof getCoveragesResponseSchema>;
